import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ITranslation } from '../core/services/translate.service';
import { DataCacheService } from '../modules/tt-data-cache/services/data-cache.service';
import { Settings, AppSettingsService } from '../core/services/app-settings.service';
import { downgradeInjectable } from '@angular/upgrade/static';
import imApp from '../../ng-app/app.module.ajs'

// Global from fallback.js (loaded via <script> in index.html).
declare var fallback: any;

/**
 * AppConfigService
 *
 * Hybrid AngularJS/Angular bootstrap service.
 *
 * Responsibilities:
 *  - Fetch /api/settings and expose them via AppSettingsService.
 *  - Enforce login redirect if the user is not authorized.
 *  - Initialize shared translation cache.
 *  - Load a set of legacy JS libraries using fallback.js (CDN-first, then local).
 *  - Patch fallback.js detection to correctly handle jQuery plugins and other
 *    “side-effect only” libraries that don’t expose standard globals.
 *  - Bridge this Angular service into AngularJS via downgradeInjectable.
 */
@Injectable()
export class AppConfigService {
    private readonly CLIENT_SESSION_ID_KEY: string = 'clientSessionId';
    private _clientSessionId: string = '';

    constructor(
        private http: HttpClient,
        private appSettings: AppSettingsService,
        private dcs: DataCacheService,
    ) {
        // BJS 20241126 - Set unique session id for this browser instance.
        // Used to correlate login redirects and server-side session state.
        this.setClientSessionIdIfNotExists();
    }

    get settings(): Settings {
        // Ensure the current session id is propagated into the settings object.
        this.appSettings.clientSessionId = this._clientSessionId;

        return this.appSettings.settings;
    }

    get clientSessionId(): string {
        return this._clientSessionId;
    }

    /**
     * Called from APP_INITIALIZER in Angular.
     * - Loads /api/settings.
     * - Redirects to login if not authorized.
     * - Initializes translation cache.
     * - Configures fallback.js detection.
     * - Loads all legacy JS libraries (CDN-first with local fallbacks).
     */
    public load(): Promise<Settings> {
        return new Promise<Settings>((resolve) => {
            const self = this;

            /**
             * Handles login redirection for different entry patterns:
             *  - /mainmenu/userlink/{company}/{token}
             *  - /mainmenu/{company}/{user}/{token}
             *  - "normal" login (no extra segments after /mainmenu).
             */
            function login() {
                const parts = window.location.href.split('/');
                const index = parts.indexOf('mainmenu');

                let url = self.appSettings.settings.loginUrl;

                if (parts.length === index + 4) {
                    if (parts[index + 1] === 'userlink') {
                        // userlink login
                        url = self.appSettings.settings.loginUrlUserlink + '/' + parts[index + 2] + '/' + parts[index + 3];
                    } else {
                        // assume this is login with credentials from app
                        url = self.appSettings.settings.loginUrlCredentials + '/' + parts[index + 1] + '/' + parts[index + 2] + '/' + parts[index + 3];
                    }

                    return;
                }

                // "normal" login
                window.location.href = `${url}?csi=${self.appSettings.clientSessionId}`;
            }

            this.http.get<Settings>('/api/settings/getsettings').subscribe({
                next: (result) => {
                    this.appSettings.settings = result;

                    if (!result.isAuthorized) {
                        // Not authorized -> immediately redirect to login.
                        login();
                        return;
                    }

                    // Initialize translation cache for the current company + language.
                    this.dcs.InitCache<ITranslation>('translations_' + result.companyId + '_' + result.user?.languageId);

                    // BJS 20251205 - Extend fallback.is_defined to handle special cases for libraries
                    // that don't follow standard loading patterns (e.g. pure jQuery plugins).
                    this.configureFallbackDetectors();

                    // Load legacy JS dependencies via fallback.js.
                    //
                    // Pattern:
                    //  - Key is the "library name" used by fallback.
                    //  - For 3rd party libs, the key usually matches the global it defines
                    //    (e.g. moment, JSZip, X2JS, SignaturePad, PhoneNumberUtil).
                    //  - For AngularJS libs, the key matches the Angular module name.
                    //  - Each value is either:
                    //      - an array [cdnUrl, localUrlWithVersion] (CDN-first, then local), or
                    //      - a single local URL for custom/modified libraries.
                    //
                    // fallback.js will:
                    //  - Try each URL in order.
                    //  - On success, mark the library as loaded.
                    //  - On failure, try the next URL.
                    //  - When all libraries have either succeeded or exhausted URLs,
                    //    invoke the callback below with success/failed maps.
                    fallback.load(
                        {
                            jQueryui: ['//cdnjs.cloudflare.com/ajax/libs/jqueryui/1.12.1/jquery-ui.min.js', './assets/js/jquery-ui/jquery-ui-1.12.1.min.js?v=' + result.version],
                            SparkMD5: ['//cdnjs.cloudflare.com/ajax/libs/spark-md5/3.0.0/spark-md5.min.js', './assets/js/spark-md5/spark-md5.min.js?v=' + result.version],
                            moment: ['//cdnjs.cloudflare.com/ajax/libs/moment.js/2.18.1/moment-with-locales.min.js', './assets/js/moment/moment-with-locales-2.18.1.min.js?v=' + result.version],
                            notify: ['//cdnjs.cloudflare.com/ajax/libs/mouse0270-bootstrap-notify/3.1.7/bootstrap-notify.min.js', './assets/js/bootstrap-notify/bootstrap-notify-3.1.5.min.js?v=' + result.version],
                            PhoneNumberUtil: './assets/js/phone-format-global/phone-format-global.min.js?v=' + result.version,
                            uuid: './assets/js/uuid/uuid.js?v=' + result.version,
                            'kendo.cultures.no': ['./assets/js/kendo.culture.no/kendo.culture.no.min.js?v=' + result.version],
                            SignaturePad: ['./assets/js/signature-pad/signature-pad.min.js?v=' + result.version],
                            tv4: ['//cdnjs.cloudflare.com/ajax/libs/tv4/1.3.0/tv4.min.js', './assets/js/tv4/tv4-1.3.0.min.js?v=' + result.version],
                            X2JS: ['//cdn.jsdelivr.net/gh/abdmob/x2js@master/xml2json.min.js', './assets/js/xml2json/xml2json.min.js?v=' + result.version],
                            JSZip: ['//cdnjs.cloudflare.com/ajax/libs/jszip/2.6.1/jszip.min.js', './assets/js/jszip/jszip.min.js?v=' + result.version],
                            Flickity: ['//cdnjs.cloudflare.com/ajax/libs/flickity/2.2.2/flickity.pkgd.min.js', './assets/js/flickity/flickity.pkgd-2.2.2.min.js?v=' + result.version],
                            Packery: ['./assets/js/packery/packery.pkgd-2.1.2.min.js?v=' + result.version],
                            'ui.router.state.events': ['//cdnjs.cloudflare.com/ajax/libs/angular-ui-router/1.0.29/stateEvents.js', './assets/js/angular-ui-router-stateevents/angular-ui-router-stateevents-1.0.29.min.js?v=' + result.version],
                            'ui.select': ['./assets/js/ui-select/ui-select.js?v=' + result.version], // BJS 20220730 - Do not use cdn for this, source has been modified
                            ngAnimate: ['//cdnjs.cloudflare.com/ajax/libs/angular-animate/1.8.3/angular-animate.min.js', './assets/js/angular-animate/angular-animate-1.8.3.min.js?v=' + result.version],
                            ngSanitize: ['//cdnjs.cloudflare.com/ajax/libs/angular-sanitize/1.8.3/angular-sanitize.min.js', './assets/js/angular-sanitize/angular-sanitize-1.8.3.min.js?v=' + result.version],
                            ngTouch: ['//cdnjs.cloudflare.com/ajax/libs/angular-touch/1.8.3/angular-touch.min.js', './assets/js/angular-touch/angular-touch-1.6.3.min.js?v=' + result.version],
                            ngFileSaver: ['./assets/js/angular-file-saver/angular-file-saver-bundle.min.js?v=' + result.version],
                            'monospaced.elastic': ['./assets/js/angular-elastic/angular-elastic-2.5.1.min.js?v=' + result.version], // BJS 20220428 - Do not use cdn for this, source has been modified
                            signature: './assets/js/signature/signature.min.js?v=' + result.version,
                            'ui.bootstrap': ['//cdnjs.cloudflare.com/ajax/libs/angular-ui-bootstrap/2.5.0/ui-bootstrap-tpls.min.js', './assets/js/ui-bootstrap/ui-bootstrap-tpls-2.5.0.min.js?v=' + result.version],
                            'ui.sortable': ['//cdnjs.cloudflare.com/ajax/libs/angular-ui-sortable/0.19.0/sortable.min.js', './assets/js/angular-ui-sortable/ui-sortable-0.19.0.min.js?v=' + result.version],
                            noUiSlider: ['//cdnjs.cloudflare.com/ajax/libs/noUiSlider/14.5.0/nouislider.min.js', './assets/js/nouislider/nouislider-14.5.0.min.js?v=' + result.version],
                            'ya.nouislider': './assets/js/ya-nouislider/ya.nouislider-2.8.0.min.js?v=' + result.version,
                            ngStorage: ['//cdnjs.cloudflare.com/ajax/libs/ngStorage/0.3.10/ngStorage.min.js', './assets/js/ng-storage/ngStorage-0.3.10.min.js?v=' + result.version],
                            ngFileUpload: ['//cdnjs.cloudflare.com/ajax/libs/danialfarid-angular-file-upload/12.2.13/ng-file-upload-all.min.js', './assets/js/ng-file-upload/ng-file-upload-all-12.2.13.min.js?v=' + result.version],
                            'angular-md5': ['./assets/js/angular-md5/angular-md5-0.1.7.min.js', '//cdnjs.cloudflare.com/ajax/libs/angular-md5/0.1.7/angular-md5.min.js?v=' + result.version],
                            'ab-base64': ['//cdnjs.cloudflare.com/ajax/libs/angular-utf8-base64/0.0.5/angular-utf8-base64.min.js', './assets/js/angular-utf8-base64/angular-uft8-base64.min.js?v=' + result.version],
                            schemaForm: './assets/js/angular-schema-form/angular-schema-form-1.0.0-alpha.5.min.js?v=' + result.version,
                            schemaFormBootstrap: './assets/js/angular-schema-form-bootstrap/angular-schema-form-bootstrap-1.0.0-alpha.5.min.js?v=' + result.version,
                            'kendo.directives': ['//kendo.cdn.telerik.com/2019.1.115/js/kendo.all.min.js', './assets/js/kendo-all/kendo.all-2019.1.115.min.js?v=' + result.version],
                            appConfig: './assets/js/app-config/app-config.min.js?v=' + result.version,
                            'ie.http': './assets/js/ie-http/ie.http.min.js?v=' + result.version,
                            'ie.screenblock': './assets/js/ie-screenblock/ie.screenblock.min.js?v=' + result.version,
                            'ie.modal': './assets/js/ie-modal/ie.modal.min.js?v=' + result.version,
                            'ie.fileserver': './assets/js/ie-fileserver/ie.fileserver.js?v=' + result.version,
                            'ie.printserver': './assets/js/ie-printserver/ie.printserver.js?v=' + result.version,
                            'ie.officeonline': './assets/js/ie-officeonline/ie.officeonline.js?v=' + result.version,
                            'tt.interceptor': './assets/js/tt-interceptor/tt.interceptor.js?v=' + result.version,
                        },
                        {
                            // Shim/dependency graph for libraries that require others to be loaded first.
                            shim: {
                                'ya.nouislider': ['noUiSlider'],
                                'ui.select': ['ngSanitize', 'ui.bootstrap'],
                                'tt.interceptor': ['ui.bootstrap', 'ab-base64'],
                                schemaForm: ['tv4'],
                                schemaFormBootstrap: ['schemaForm'],
                                'ie.officeonline': ['ie.modal'],
                                'kendo.cultures.no': ['kendo.directives'],
                            },
                            callback: function (success: any, failed: any) {
                                // Log any libraries that completely failed to load (all URLs exhausted).
                                const failedKeys = Object.keys(failed || {});

                                if (failedKeys.length) {
                                    console.warn('fallback.js: some libraries failed to load:');

                                    for (const key of failedKeys) {
                                        const urls = failed[key];

                                        if (Array.isArray(urls) && urls.length) {
                                            // One line per failed library with all attempted URLs.
                                            console.warn(`  - ${key}:`, urls);
                                        } else {
                                            console.warn(`  - ${key}: (no URLs recorded)`);
                                        }
                                    }

                                    // Highlight a small set of "critical" libraries where failure is likely fatal.
                                    const critical = ['moment', 'SparkMD5', 'ngAnimate', 'ngSanitize'];
                                    const missingCritical = critical.filter((k) => failedKeys.indexOf(k) >= 0);

                                    if (missingCritical.length) {
                                        console.error('Critical libraries missing:', missingCritical);
                                        // If needed, this is where we could surface a user-facing error/toast.
                                    }
                                }

                                // Signal that the app configuration (settings + legacy libs) is ready.
                                self.appSettings.setIsReady();
                                resolve(self.appSettings.settings);
                            },
                        },
                    );
                },
                error: (e) => console.error(e),
            });
        });
    }

    /**
     * Generates and persists a per-browser-instance session id.
     * Used to tag login flows and correlate with server-side sessions.
     */
    private setClientSessionIdIfNotExists(): string {
        let clientSessionId = this.getClientSessionId();

        if (!clientSessionId || clientSessionId === null) {
            clientSessionId = this.generateUniqueId();
            this.setClientSessionId(clientSessionId);
        }

        this._clientSessionId = clientSessionId;
        this.appSettings.clientSessionId = clientSessionId;

        return clientSessionId;
    }

    private setClientSessionId(clientSessionId: string) {
        sessionStorage.setItem(this.CLIENT_SESSION_ID_KEY, clientSessionId);
    }

    private getClientSessionId(): string | null {
        return sessionStorage.getItem(this.CLIENT_SESSION_ID_KEY);
    }

    /**
     * Generate a 256-bit random id (hex-encoded).
     * Used as clientSessionId to avoid collisions between tabs/browsers.
     */
    private generateUniqueId(): string {
        const array = new Uint8Array(32); // 32 bytes = 256 bits

        window.crypto.getRandomValues(array);

        return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    /**
     * BJS 20251205 - Extend fallback.is_defined to handle special cases for libraries
     * that don't follow standard loading patterns.
     *
     * By default, fallback.is_defined(name) checks:
     *  - window[name]
     *  - angular.module(name)
     *
     * This works for:
     *  - Globals like moment, JSZip, X2JS, SignaturePad, PhoneNumberUtil, etc.
     *  - AngularJS modules like ngAnimate, ui.bootstrap, schemaForm, etc.
     *
     * It does NOT work for:
     *  - jQuery plugins that only patch $.fn or $.ui (jQuery UI, bootstrap-notify, etc.).
     *
     * This wrapper adds explicit checks for those plugin-style libraries and
     * delegates all other names to the original implementation.
     */
    private configureFallbackDetectors(): void {
        if (!fallback) return;

        // Don't patch twice if load() is called multiple times in the lifetime of the app.
        if ((fallback as any)._ttExtendedIsDefined) return;

        const originalIsDefined = fallback.is_defined.bind(fallback);

        fallback.is_defined = function (variable: string) {
            const win = window as any;
            const $ = win.jQuery;

            switch (variable) {
                case 'jQueryui':
                    // jQuery UI: presence of $.ui with a version is the canonical success signal.
                    return !!($ && $.ui && $.ui.version);

                case 'notify':
                    // bootstrap-notify: attaches $.notify or $.fn.notify.
                    return !!($ && ($.notify || ($.fn && $.fn.notify)));

                // Optional: Uncomment if the "signature" lib ever needs special handling
                // (i.e. if it is not an Angular module and doesn't expose a window.signature global).
                //
                // case 'signature':
                //     return !!win.signature;

                default:
                    // Everything else: defer to the original is_defined implementation
                    // which handles window globals + AngularJS modules.
                    return originalIsDefined(variable);
            }
        };

        (fallback as any)._ttExtendedIsDefined = true;
    }
}

// BJS 20260313 - Moved downgrades to separate file
// Imports all downgraded Angular components and services so they are registered in the AngularJS app and can be used from legacy code.
imApp.factory('appConfigService', downgradeInjectable(AppConfigService));

// 20260909 PAH - Import moved to main.ts, to prevent circular imports when running tests
// import '../core/services/downgraded.service';
